/**
 * CalculatorStep — интерактивный слайдер доходности.
 *
 * Юзер крутит слайдер номинала контракта в диапазоне Venture Slot
 * ($100, $300, $500, $1 000, $2 000, $5 000, $10 000, $25 000, $50 000)
 * и видит в реальном времени hypothetical gain за 30 дней.
 *
 * Формула (приближение, по ТЗ ~12-13%/мес):
 *   monthly_rate = 0.12 при амт < $1 000,
 *                  0.125 при $1 000..$5 000,
 *                  0.13 при $10 000..$50 000
 *   earn30 = amount * monthly_rate
 *
 * Это НЕ реальный расчёт (у реального есть дневные ставки с range),
 * но идея "крутил слайдер → показали X" работает на вовлечение.
 */
import { useState } from 'react';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';
import { CountUpNumber } from '../CountUpNumber';
import { SPRING_CARD, EASE_SCREEN } from '../springs';
import { useSounds } from '../useSounds';

interface CalculatorStepProps {
  strategy: 'passive' | 'growth' | 'network' | null;
  onAmountChange: (amount: number) => void;
  onNetworkChange: (withNetwork: boolean) => void;
  withNetwork: boolean;
  onNext: () => void;
}

// Пресеты сумм для демо (по ТЗ админа — 5 значений).
// Доходность единая ~15%/мес — это средняя реальная по Luna.
const SLOT_AMOUNTS = [1000, 3000, 5000, 7000, 10000];

function monthlyRate(_amount: number): number {
  // Везде 15% — админ попросил единую доходность для демо,
  // чтобы юзер сразу видел линейную зависимость от номинала.
  void _amount;
  return 0.15;
}

export function CalculatorStep({
  strategy,
  onAmountChange,
  onNetworkChange,
  withNetwork,
  onNext,
}: CalculatorStepProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'ru' ? 'ru-RU' : 'en-US';
  const { haptic } = useTelegramWebApp();
  const sounds = useSounds();
  const [idx, setIdx] = useState(2); // default $5000 (середина шкалы)
  const amount = SLOT_AMOUNTS[idx] ?? 1000;
  const monthly = amount * monthlyRate(amount);

  const strategyLabel =
    strategy === 'growth'
      ? t('onboarding.strategy.growthTitle')
      : strategy === 'network'
        ? t('onboarding.strategy.networkTitle')
        : t('onboarding.strategy.passiveTitle');

  const handleSlide = (newIdx: number) => {
    if (newIdx === idx) return;
    haptic.selection();
    sounds.coin();
    setIdx(newIdx);
    const next = SLOT_AMOUNTS[newIdx] ?? 1000;
    onAmountChange(next);
  };

  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center px-5 py-12 gap-8">
      <div className="text-center">
        <motion.p
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 0.8, y: 0 }}
          transition={EASE_SCREEN}
          className="font-mono"
          style={{
            fontSize: 10,
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            color: 'rgba(220,230,255,0.6)',
          }}
        >
          {t('onboarding.calc.kicker', { strategy: strategyLabel })}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...EASE_SCREEN, delay: 0.1 }}
          className="mt-3 text-[22px] leading-tight font-bold max-w-[300px]"
          style={{
            color: 'rgba(245,250,255,0.96)',
            letterSpacing: '-0.02em',
          }}
        >
          {t('onboarding.calc.title')}
        </motion.h2>
      </div>

      {/* Главная цифра — доход за 30 дней */}
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ ...SPRING_CARD, delay: 0.2 }}
        className="text-center"
      >
        <p
          className="font-mono"
          style={{
            fontSize: 10,
            letterSpacing: '0.22em',
            textTransform: 'uppercase',
            color: 'rgba(200,210,230,0.55)',
          }}
        >
          {t('onboarding.calc.in30')}
        </p>
        <CountUpNumber
          key={amount}
          value={monthly}
          prefix="+$"
          decimals={2}
          className="block mt-1 font-display font-extrabold"
        />
        <style>{`
          .font-display { font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', system-ui, sans-serif; }
        `}</style>
      </motion.div>

      {/* Слайдер номинала */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...SPRING_CARD, delay: 0.4 }}
        className="w-full max-w-[340px] flex flex-col items-center gap-4"
      >
        <p
          className="font-display font-bold"
          style={{
            fontSize: 28,
            color: 'rgba(245,250,255,0.98)',
            letterSpacing: '-0.03em',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          ${amount.toLocaleString(locale)}
        </p>
        <input
          type="range"
          min={0}
          max={SLOT_AMOUNTS.length - 1}
          step={1}
          value={idx}
          onChange={(e) => handleSlide(Number(e.target.value))}
          className="w-full h-2 appearance-none rounded-full cursor-pointer"
          style={{
            background:
              'linear-gradient(to right, rgba(234,196,96,0.8) 0%, rgba(234,196,96,0.8) ' +
              ((idx / (SLOT_AMOUNTS.length - 1)) * 100).toFixed(0) +
              '%, rgba(255,255,255,0.12) ' +
              ((idx / (SLOT_AMOUNTS.length - 1)) * 100).toFixed(0) +
              '%, rgba(255,255,255,0.12) 100%)',
          }}
        />
        <div
          className="w-full flex justify-between text-[9px] font-mono opacity-60"
          style={{ color: 'rgba(200,210,230,0.7)' }}
        >
          <span>{t('onboarding.calc.rangeLow')}</span>
          <span>{t('onboarding.calc.rangeHigh')}</span>
        </div>
      </motion.div>

      {/* Тоггл «Развивать партнёрскую сеть» — решение принимается
          ДО запуска демо, чтобы анимация сразу ехала по выбранному
          сценарию, без перезапусков. */}
      <motion.button
        type="button"
        onClick={() => {
          haptic.medium();
          onNetworkChange(!withNetwork);
        }}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...SPRING_CARD, delay: 0.5 }}
        className="flex items-center justify-between gap-3 rounded-2xl
                   px-4 py-3 transition active:scale-[0.98] w-full
                   max-w-[340px]"
        style={{
          background: withNetwork
            ? 'linear-gradient(180deg, rgba(124,92,255,0.22), rgba(124,92,255,0.10))'
            : 'rgba(255,255,255,0.04)',
          border: `1.5px solid ${withNetwork
            ? 'rgba(124,92,255,0.5)'
            : 'rgba(255,255,255,0.1)'}`,
          boxShadow: withNetwork
            ? '0 10px 24px -10px rgba(124,92,255,0.5)'
            : 'none',
        }}
      >
        <div className="flex items-center gap-2 text-left">
          <span style={{ fontSize: 22 }}>👥</span>
          <div>
            <div
              className="font-bold text-[13px] leading-tight"
              style={{
                color: withNetwork ? '#E9E4FF' : 'rgba(220,230,255,0.9)',
              }}
            >
              {t('onboarding.calc.networkTitle')}
            </div>
            <div
              className="text-[10px] mt-0.5"
              style={{ color: 'rgba(200,210,230,0.55)' }}
            >
              {withNetwork
                ? t('onboarding.calc.networkOn')
                : t('onboarding.calc.networkOff')}
            </div>
          </div>
        </div>
        {/* iOS-style toggle */}
        <span
          className="relative inline-flex h-7 w-12 shrink-0 items-center
                     rounded-full transition"
          style={{
            background: withNetwork
              ? 'linear-gradient(180deg, #9B7FFF 0%, #6A4BFF 100%)'
              : 'rgba(255,255,255,0.12)',
            boxShadow: withNetwork
              ? '0 0 14px -2px rgba(124,92,255,0.6)'
              : 'inset 0 1px 2px rgba(0,0,0,0.2)',
          }}
        >
          <span
            className="inline-block h-5 w-5 rounded-full bg-white shadow transition"
            style={{
              transform: withNetwork ? 'translateX(24px)' : 'translateX(4px)',
            }}
          />
        </span>
      </motion.button>

      {/* Далее */}
      <motion.button
        type="button"
        onClick={() => {
          haptic.medium();
          sounds.swipe();
          onNext();
        }}
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...SPRING_CARD, delay: 0.6 }}
        whileTap={{ scale: 0.97 }}
        className="px-8 py-4 rounded-full font-semibold"
        style={{
          minWidth: 240,
          fontSize: 15,
          color: '#06091c',
          background: 'linear-gradient(180deg, #f5e6a8 0%, #e9c76a 100%)',
          border: '1px solid rgba(255,230,150,0.5)',
          boxShadow:
            '0 10px 30px -8px rgba(234,196,96,0.5), inset 0 1px 0 rgba(255,255,255,0.5)',
          cursor: 'pointer',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        {t('onboarding.calc.cta')}
      </motion.button>
    </div>
  );
}
